import {
  AuditOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  FormOutlined,
  HistoryOutlined,
  SwapOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import RowActionsMenu from 'ui/RowActions/RowActionsMenu';
import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import { Modal } from 'antd';
// Constants
import {
  CampaignVersion,
  dateTimeFormat,
  DEFAULT_DELIVERABLE_REVISION_LIMIT,
} from 'constants/global';
// Api
import getEnvVariables from 'api/env';
import {
  GET_CAMPAIGN_OFFERS,
  GET_DELIVERABLE_REVISION_LIMIT,
} from 'api/campaignV2/queries';
import {
  COMPLETED_CAMPAIGN_OFFER,
  REMOVE_OFFER_FROM_CAMPAIGN,
} from 'api/campaignV2/mutations';
// Types
import { GetCampaignsV2_getCampaigns_entities } from 'api/campaignV2/types/GetCampaignsV2';
import {
  CompleteCampaignOffer,
  CompleteCampaignOfferVariables,
} from 'api/campaignV2/types/CompleteCampaignOffer';
import {
  RemoveOfferFromCampaign,
  RemoveOfferFromCampaignVariables,
} from 'api/campaignV2/types/RemoveOfferFromCampaign';
import {
  GetCampaignOffers,
  GetCampaignOffers_getCampaignOffers_entities,
  GetCampaignOffersVariables,
} from 'api/campaignV2/types/GetCampaignOffers';
import {
  CampaignOffersOrderBy,
  CampaignOfferStatus,
  CampaignStatus,
  CampaignType,
  GetCampaignOffersInput,
  SortDirection,
  CbDeliverableV2Status,
} from 'api/graphql-global-types';
import { GetDeliverableRevisionLimit } from 'api/campaignV2/types/GetDeliverableRevisionLimit';
// Helpers
import { getSocialMediaType } from 'helpers/following';
import {
  CAMPAIGN_OFFER_STATUS_OPTIONS,
  canRemoveTalentFromCampaign,
  getFollowerRange,
  isCustomLikeCampaign,
  isOnlyContentDeliverables,
  mapOfferStatusToLabel,
  SOCIAL_FOLLOWERS_OPTIONS,
} from 'helpers/campaign';
import { hasCounterOffer } from 'helpers/counterOffer';
// UI
import Table, { SortedInfo, TableFilter } from 'ui/Table';
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import Followers from '../Followers/Followers';
import SearchByNameAndEmail from './components/SearchByNameAndEmail/SearchByNameAndEmail';
import CampaignDeliverables from './components/CampaignDeliverables/CampaignDeliverables';
import CampaignDeliverablesReview from './components/CampaignDeliverablesReview/CampaignDeliverablesReview';
import ManageTalentAnswersModal from './components/ManageTalentAnswersModal/ManageTalentAnswersModal';
import TimelineDeliverablesReviewModal from './components/CampaignDeliverablesReview/TimelineDeliverablesReviewModal/TimelineDeliverablesReviewModal';
import CounterOfferModal from './components/CounterOfferModal/CounterOfferModal';
// Styles
import styles from './TalentTable.module.scss';

type TalentTableProps = {
  record: GetCampaignsV2_getCampaigns_entities;
};

const TalentTable = ({ record }: TalentTableProps): JSX.Element | null => {
  const { data: revisionLimitData } = useQuery<GetDeliverableRevisionLimit>(
    GET_DELIVERABLE_REVISION_LIMIT
  );

  const deliverableRevisionLimit =
    revisionLimitData?.getDeliverableRevisionLimit ||
    DEFAULT_DELIVERABLE_REVISION_LIMIT;
  // deliverables submission is a final step for talents, where they submit approved deliverables
  const [deliverablesSubmissionModalData, setDeliverablesSubmissionModalData] =
    useState<GetCampaignOffers_getCampaignOffers_entities | null>(null);

  // deliverables review is where review data is saved until publish
  const [deliverablesReviewModalData, setDeliverablesReviewModalData] =
    useState<GetCampaignOffers_getCampaignOffers_entities | null>(null);

  // used on deliverables review modal as a flag for child component
  const [isRevisionHistoryReadOnly, setIsRevisionHistoryReadOnly] =
    useState<boolean>(false);

  const [manageTalentAnswersModalData, setManageTalentAnswersModalData] =
    useState<GetCampaignOffers_getCampaignOffers_entities | null>(null);

  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchFilters, setSearchFilters] = useState<TableFilter[]>([]);
  const [sort, setSortInfo] = useState<SortedInfo<CampaignOffersOrderBy>>({
    order: SortDirection.DESC,
    key: CampaignOffersOrderBy.name,
  });

  const [v3ReviewModalData, setV3ReviewModalData] =
    useState<GetCampaignOffers_getCampaignOffers_entities | null>(null);

  // Counter offer modal state
  const [counterOfferModalData, setCounterOfferModalData] =
    useState<GetCampaignOffers_getCampaignOffers_entities | null>(null);

  const isSocialCampaign = record?.type === CampaignType.Social;

  const socialMediaType = useMemo(() => {
    return record ? getSocialMediaType(record) : null;
  }, [record]);

  const recordId = record?.id;

  const getCampaignOffersInput = useMemo(() => {
    const input: GetCampaignOffersInput = {
      campaignId: recordId,
      direction: sort.order,
      orderBy: sort.key,
      limit: pageSize,
      offset: (currentPage > 1 ? currentPage - 1 : 0) * pageSize,
      ...(isSocialCampaign && {
        socialMediaType,
      }),
    };

    searchFilters?.forEach(({ key, value }) => {
      if (key === CampaignOffersOrderBy.status) {
        return (input['statusesV2'] = [value] as CampaignOfferStatus[]);
      }
      if (key === CampaignOffersOrderBy.socialFollowingLive) {
        const result = getFollowerRange(value as string);

        return (input['socialFollowingLiveRange'] = result);
      }
    });

    return { input };
  }, [
    recordId,
    sort.order,
    sort.key,
    pageSize,
    currentPage,
    isSocialCampaign,
    socialMediaType,
    searchFilters,
  ]);

  const {
    data: offerData,
    loading,
    refetch,
  } = useQuery<GetCampaignOffers, GetCampaignOffersVariables>(
    GET_CAMPAIGN_OFFERS,
    {
      variables: getCampaignOffersInput,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: !record?.id,
    }
  );
  const [completeCampaignOffer] = useMutation<
    CompleteCampaignOffer,
    CompleteCampaignOfferVariables
  >(COMPLETED_CAMPAIGN_OFFER);

  const [removeOfferFromCampaign] = useMutation<
    RemoveOfferFromCampaign,
    RemoveOfferFromCampaignVariables
  >(REMOVE_OFFER_FROM_CAMPAIGN);

  const handleCloseDeliverablesSubmissionModal = useCallback(() => {
    setDeliverablesSubmissionModalData(null);
  }, []);

  const handleCloseDeliverablesReviewModal = useCallback(() => {
    setDeliverablesReviewModalData(null);
  }, []);

  const handleCloseManageTalentAnswersModal = useCallback(() => {
    setManageTalentAnswersModalData(null);
  }, []);

  const handleCloseV3ReviewModal = useCallback(() => {
    setV3ReviewModalData(null);
  }, []);

  const handleFinish = useCallback(() => {
    setDeliverablesReviewModalData(null);
    handleCloseDeliverablesSubmissionModal();
    handleCloseDeliverablesReviewModal();
  }, [
    handleCloseDeliverablesSubmissionModal,
    handleCloseDeliverablesReviewModal,
  ]);

  const talents = offerData?.getCampaignOffers?.entities;

  const handleCompleteCampaignOffer = useCallback(
    async (offer: GetCampaignOffers_getCampaignOffers_entities) => {
      try {
        await completeCampaignOffer({
          variables: {
            input: {
              ownerId: offer.campaign.agency.id,
              offerId: offer.id,
            },
          },
        });

        successNotification('Campaign offer completed!');
      } catch (err) {
        errorNotification((err as Error)?.message);
      }
    },
    [completeCampaignOffer]
  );

  const handleRemoveOffer = useCallback(
    (offer: GetCampaignOffers_getCampaignOffers_entities) => {
      Modal.confirm({
        title: `Remove ${
          offer.profileName || 'this talent'
        } from the campaign?`,
        icon: <ExclamationCircleOutlined />,
        content:
          'They will not be invited to this campaign again. An admin can add them back with talent search.',
        okText: 'Remove',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await removeOfferFromCampaign({
              variables: { input: { offerId: offer.id } },
            });

            successNotification('Talent removed from campaign');
            refetch();
          } catch (err) {
            errorNotification((err as Error)?.message);
          }
        },
      });
    },
    [removeOfferFromCampaign, refetch]
  );

  const handleOpenDeliverablesSubmissionModal = useCallback(
    (record: GetCampaignOffers_getCampaignOffers_entities) => {
      setDeliverablesSubmissionModalData(record);
    },
    []
  );

  const handleOpenDeliverablesReviewModal = useCallback(
    (record: GetCampaignOffers_getCampaignOffers_entities) => {
      setDeliverablesReviewModalData(record);
      setIsRevisionHistoryReadOnly(false);
    },
    []
  );

  const handleOpenRevisionHistoryModal = useCallback(
    (record: GetCampaignOffers_getCampaignOffers_entities) => {
      setDeliverablesReviewModalData(record);
      setIsRevisionHistoryReadOnly(true);
    },
    []
  );

  const handleOpenManageTalentAnswersModal = useCallback(
    (record: GetCampaignOffers_getCampaignOffers_entities) => {
      setManageTalentAnswersModalData(record);
    },
    []
  );

  const handleOpenV3ReviewModal = useCallback(
    (record: GetCampaignOffers_getCampaignOffers_entities) => {
      setV3ReviewModalData(record);
    },
    []
  );

  // Counter offer handlers
  const handleOpenCounterOfferModal = useCallback(
    (record: GetCampaignOffers_getCampaignOffers_entities) => {
      setCounterOfferModalData(record);
    },
    []
  );

  const handleCloseCounterOfferModal = useCallback(() => {
    setCounterOfferModalData(null);
  }, []);

  const handleCounterOfferSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const columns = useMemo(
    () => [
      {
        title: 'Talent id',
        dataIndex: 'userId',
        key: CampaignOffersOrderBy.id,
        align: 'left' as const,
        width: 150,
        sorterType: 'text',
      },
      {
        title: 'Talent Name',
        dataIndex: 'profileName',
        key: CampaignOffersOrderBy.name,
        align: 'left' as const,
        width: 150,
        sorterType: 'text',
      },
      {
        title: 'Following',
        dataIndex: 'socialFollowingLive',
        key: CampaignOffersOrderBy.socialFollowingLive,
        align: 'left' as const,
        width: 150,
        sorterType: 'number',
        withRadioFilters: true,
        filters: SOCIAL_FOLLOWERS_OPTIONS,
        render: function DisplayFollowers(
          dueData: string,
          item: GetCampaignOffers_getCampaignOffers_entities
        ) {
          return <Followers row={item} />;
        },
      },
      {
        title: 'Application Date',
        dataIndex: 'appliedAt',
        key: CampaignOffersOrderBy.appliedAt,
        align: 'left' as const,
        width: 150,
        sorterType: 'date',
        render: function DisplayDate(
          dueData: string,
          item: GetCampaignOffers_getCampaignOffers_entities
        ) {
          return (
            <span>
              {item.appliedAt
                ? moment(item.appliedAt).local().format(dateTimeFormat)
                : 'N/A'}
            </span>
          );
        },
      },
      {
        title: 'Approved Date',
        dataIndex: 'approvedAt',
        key: CampaignOffersOrderBy.approvedAt,
        align: 'left' as const,
        width: 150,
        sorterType: 'date',
        render: function DisplayDate(
          dueData: string,
          item: GetCampaignOffers_getCampaignOffers_entities
        ) {
          return (
            <span>
              {item.approvedAt
                ? moment(item.approvedAt).local().format('MM/DD/YYYY, h:mm a')
                : 'N/A'}
            </span>
          );
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: CampaignOffersOrderBy.status,
        align: 'left' as const,
        width: 150,
        withRadioFilters: true,
        filters: CAMPAIGN_OFFER_STATUS_OPTIONS,
        filterMultiple: true,
        render: function displayStatus(
          status: string,
          item: GetCampaignOffers_getCampaignOffers_entities
        ) {
          return <span>{mapOfferStatusToLabel(item.statusV2)}</span>;
        },
      },
      {
        title: 'Submission Date',
        dataIndex: 'submittedAt',
        key: CampaignOffersOrderBy.submittedAt,
        align: 'left' as const,
        width: 150,
        sorterType: 'date',
        render: function DisplayDate(
          dueData: string,
          item: GetCampaignOffers_getCampaignOffers_entities
        ) {
          return (
            <span>
              {item.submittedAt
                ? moment(item.submittedAt).local().format('MM/DD/YYYY, h:mm a')
                : 'N/A'}
            </span>
          );
        },
      },
      {
        title: 'Actions',
        dataIndex: 'actions',
        align: 'center' as const,
        width: 80,
        render: function Actions(
          id: string,
          record: GetCampaignOffers_getCampaignOffers_entities
        ) {
          const isV3Campaign = record.campaign.v === CampaignVersion.V3;

          const isCampaignPublished =
            record?.statusV2 === CampaignOfferStatus.PUBLISHED;

          const isBookMeCampaign = record.campaign.type === CampaignType.BookMe;

          const isLiveAppearance =
            record?.submissions &&
            record.submissions[0]?.__typename ===
              'CampaignLiveAppearanceSubmission';

          const showViewButton =
            (isCampaignPublished ||
              record?.statusV2 === CampaignOfferStatus.COMPLETED) &&
            !isBookMeCampaign &&
            !isLiveAppearance;
          const isDeliverableApproved =
            record.statusV2 === CampaignOfferStatus.DELIVERABLES_APPROVED;

          const isScheduled = record.statusV2 === CampaignOfferStatus.SCHEDULED;

          const isSocialShareCampaign =
            record.campaign.type === CampaignType.SocialShare;

          const isSponsoredAppearanceCampaign =
            record.campaign.type === CampaignType.SponsoredAppearance;

          // don't show Manage Talent Answers button for BookMe / SocialShare campaigns
          const shouldShowAnswersButton =
            !isBookMeCampaign && !isSocialShareCampaign;

          const allowSubmitDeliverables =
            (isDeliverableApproved || isScheduled) &&
            !isLiveAppearance &&
            !isV3Campaign &&
            !isBookMeCampaign;

          const showSocialShareSubmitDeliverablesButton =
            isSocialShareCampaign &&
            record.statusV2 === CampaignOfferStatus.ACCEPTED;

          const showSponsoredAppearanceSubmitDeliverablesButton =
            isSponsoredAppearanceCampaign &&
            record.statusV2 === CampaignOfferStatus.ACCEPTED &&
            !isLiveAppearance;

          const isDeliverableRejected =
            record.statusV2 === CampaignOfferStatus.DELIVERABLES_REJECTED;

          const isLimitReached =
            record.deliverables?.length === deliverableRevisionLimit + 1;

          // revision history is read only if it is accepted or all 3 are rejected
          const isRevisionHistoryReadOnly =
            (isLimitReached && isDeliverableRejected) || isDeliverableApproved;

          // show revision history button if offer is not live appearance and history is read only
          const showRevisionHistoryButton =
            !isLiveAppearance && isRevisionHistoryReadOnly;

          // if campaign is in submitted state, that means a review is ready and waiting
          const showReviewDeliverablesButton =
            record.statusV2 === CampaignOfferStatus.SUBMITTED &&
            // V3 campaigns have review form inside submit modal for better overview
            !isV3Campaign;

          const isContentOnlyType = isOnlyContentDeliverables(
            record.campaign.type
          );

          // if campaign is in accepted or deliverables_rejected state and limit is not reached
          // we can upload new deliverables for review
          let showUploadDeliverablesForReviewButton =
            ((record.statusV2 === CampaignOfferStatus.ACCEPTED ||
              isDeliverableRejected) &&
              !isLimitReached &&
              !isSponsoredAppearanceCampaign &&
              !isBookMeCampaign &&
              !isV3Campaign) ||
            // Show button for V3 campaigns when there are incomplete deliverables
            (isV3Campaign &&
              record.deliverablesV2?.some(
                (deliverable) =>
                  deliverable.status !== CbDeliverableV2Status.PUBLISHED
              ));

          // If its only content no upload deliverables for review button should be shown if the deliverables are approved
          if (
            isContentOnlyType &&
            record.statusV2 === CampaignOfferStatus.DELIVERABLES_APPROVED
          ) {
            showUploadDeliverablesForReviewButton = false;
          }

          const hasV3DeliverablesForReview =
            isV3Campaign &&
            record.deliverablesV2?.some(
              (deliverable) =>
                deliverable.status === CbDeliverableV2Status.ON_BRAND_REVIEW
            );

          // Counter offer logic
          const supportsCounterOffer = hasCounterOffer(record);
          const isInvited = record.statusV2 === CampaignOfferStatus.INVITED;
          const hasCounterOfferAttention = !!record.counterOfferAttention;
          const hasExistingCounterOffers =
            record.counterOffers && record.counterOffers.length > 0;

          // Show counter offer button when:
          // 1. Campaign supports counter offers AND
          // 2. Either status is INVITED (initial counter) OR counterOfferAttention is set OR has existing counter offers
          const showCounterOfferButton =
            supportsCounterOffer &&
            (isInvited || hasCounterOfferAttention || hasExistingCounterOffers);

          const isCounterOfferOpen = [
            CampaignOfferStatus.INVITED,
            CampaignOfferStatus.COUNTER_OFFER_PENDING,
          ].includes(record.statusV2);

          return (
            <RowActionsMenu
              label={`Actions for ${record.profileName || 'talent'}`}
              actions={[
                {
                  key: 'manage-answers',
                  label: 'Manage talent answers',
                  icon: <FormOutlined />,
                  hidden: !shouldShowAnswersButton,
                  onClick: () => handleOpenManageTalentAnswersModal(record),
                },
                {
                  key: 'counter-offer',
                  label: isCounterOfferOpen
                    ? 'Counter offer'
                    : 'Counter offer history',
                  icon: <SwapOutlined />,
                  hidden: !showCounterOfferButton,
                  onClick: () => handleOpenCounterOfferModal(record),
                },
                {
                  key: 'revision-history',
                  label: 'Revision history',
                  icon: <HistoryOutlined />,
                  hidden: !showRevisionHistoryButton,
                  onClick: () => handleOpenRevisionHistoryModal(record),
                },
                {
                  key: 'review-deliverables',
                  label: 'Review deliverables',
                  icon: <AuditOutlined />,
                  startsGroup: true,
                  hidden:
                    !showReviewDeliverablesButton || isSocialShareCampaign,
                  onClick: () => handleOpenDeliverablesReviewModal(record),
                },
                {
                  key: 'v3-review-deliverables',
                  label: 'Review deliverables',
                  icon: <AuditOutlined />,
                  startsGroup: true,
                  hidden: !hasV3DeliverablesForReview,
                  onClick: () => handleOpenV3ReviewModal(record),
                },
                {
                  key: 'upload-deliverables',
                  label: 'Upload deliverables for review',
                  icon: <UploadOutlined />,
                  hidden:
                    !showUploadDeliverablesForReviewButton ||
                    isSocialShareCampaign,
                  onClick: () => handleOpenDeliverablesReviewModal(record),
                },
                {
                  key: 'submit-deliverables',
                  label: 'Submit deliverables',
                  icon: <UploadOutlined />,
                  hidden: !(
                    allowSubmitDeliverables ||
                    showSocialShareSubmitDeliverablesButton ||
                    showSponsoredAppearanceSubmitDeliverablesButton
                  ),
                  onClick: () => handleOpenDeliverablesSubmissionModal(record),
                },
                {
                  key: 'view-deliverables',
                  label: 'View submitted deliverables',
                  icon: <EyeOutlined />,
                  hidden: !showViewButton,
                  onClick: () => handleOpenDeliverablesSubmissionModal(record),
                },
                {
                  key: 'download-agreement',
                  label: 'Download digital agreement',
                  icon: <FileTextOutlined />,
                  hidden: !(
                    isCustomLikeCampaign(record.campaign.type) &&
                    record.hasSignedAgreement
                  ),
                  href: `${getEnvVariables.CAMPAIGN_APP_URL.replace(
                    /\/$/,
                    ''
                  )}/campaign-offer-agreement/${record.id}`,
                },
                {
                  key: 'complete-offer',
                  label: 'Complete campaign offer',
                  icon: <CheckCircleOutlined />,
                  positive: true,
                  startsGroup: true,
                  hidden: !isCampaignPublished,
                  onClick: () => handleCompleteCampaignOffer(record),
                },
                {
                  key: 'remove-talent',
                  label: 'Remove from campaign',
                  icon: <DeleteOutlined />,
                  danger: true,
                  startsGroup: true,
                  hidden: !canRemoveTalentFromCampaign(
                    record.campaign.type,
                    record.statusV2
                  ),
                  onClick: () => handleRemoveOffer(record),
                },
              ]}
            />
          );
        },
      },
    ],
    [
      handleCompleteCampaignOffer,
      handleOpenRevisionHistoryModal,
      handleOpenDeliverablesReviewModal,
      handleOpenDeliverablesSubmissionModal,
      handleOpenManageTalentAnswersModal,
      deliverableRevisionLimit,
      handleOpenV3ReviewModal,
      handleOpenCounterOfferModal,
      handleRemoveOffer,
    ]
  );

  const showTalentTable =
    record?.type === CampaignType.Social ||
    record?.type === CampaignType.SocialShare ||
    record?.type === CampaignType.SponsoredAppearance ||
    record?.type === CampaignType.BookMe ||
    record?.type === CampaignType.Ugc ||
    isCustomLikeCampaign(record?.type);

  // Don't show talent selection for direct booking campaigns or campaigns
  // without a fitting status.
  const showTalentAdd =
    record?.type !== CampaignType.DirectBooking &&
    showTalentTable &&
    (record?.status === CampaignStatus.ON_REVIEW ||
      record?.status === CampaignStatus.DETAIL_FILLED ||
      record?.status === CampaignStatus.PAID_AND_ACTIVE);

  // Add this function to get the current offer data - move it to the top level
  const getCurrentOfferData = useCallback(() => {
    if (
      !deliverablesReviewModalData ||
      !offerData?.getCampaignOffers?.entities
    ) {
      return deliverablesReviewModalData;
    }

    // Find the updated offer data from the fresh query result
    const currentOffer = offerData.getCampaignOffers.entities.find(
      (offer) => offer.id === deliverablesReviewModalData.id
    );

    return currentOffer || deliverablesReviewModalData;
  }, [deliverablesReviewModalData, offerData]);

  // Add similar function for V3 review modal
  const getCurrentV3OfferData = useCallback(() => {
    if (!v3ReviewModalData || !offerData?.getCampaignOffers?.entities) {
      return v3ReviewModalData;
    }

    // Find the updated offer data from the fresh query result
    const currentOffer = offerData.getCampaignOffers.entities.find(
      (offer) => offer.id === v3ReviewModalData.id
    );

    return currentOffer || v3ReviewModalData;
  }, [v3ReviewModalData, offerData]);

  if (!record) {
    return null;
  }

  return (
    <div className={styles.root}>
      {showTalentAdd && (
        <SearchByNameAndEmail record={record} onAdd={refetch} />
      )}

      {showTalentTable && (
        <Table<
          GetCampaignOffers_getCampaignOffers_entities,
          CampaignOffersOrderBy
        >
          columns={columns}
          data={talents}
          scroll={{ x: 300 }}
          loading={loading && !talents?.length}
          total={offerData?.getCampaignOffers.total}
          setPageSize={setPageSize}
          setCurrentPage={setCurrentPage}
          searchFilters={searchFilters}
          setSearchFilters={setSearchFilters}
          sortInfo={sort}
          setSortInfo={setSortInfo}
        />
      )}

      <CampaignDeliverables
        record={deliverablesSubmissionModalData}
        onClose={handleCloseDeliverablesSubmissionModal}
        onFinish={handleFinish}
      />

      {deliverablesReviewModalData ? (
        <CampaignDeliverablesReview
          offer={getCurrentOfferData() || deliverablesReviewModalData} // Use the current offer data instead of snapshot
          isRevisionHistoryReadOnly={isRevisionHistoryReadOnly}
          onClose={handleCloseDeliverablesReviewModal}
          onFinish={handleFinish}
          refetch={refetch}
        />
      ) : null}

      {manageTalentAnswersModalData ? (
        <ManageTalentAnswersModal
          offer={manageTalentAnswersModalData}
          onClose={handleCloseManageTalentAnswersModal}
        />
      ) : null}

      {v3ReviewModalData ? (
        <TimelineDeliverablesReviewModal
          offer={getCurrentV3OfferData() || v3ReviewModalData}
          onClose={handleCloseV3ReviewModal}
          onFinish={handleFinish}
          refetch={refetch}
        />
      ) : null}

      {counterOfferModalData ? (
        <CounterOfferModal
          offer={counterOfferModalData}
          visible={!!counterOfferModalData}
          onClose={handleCloseCounterOfferModal}
          onSuccess={handleCounterOfferSuccess}
        />
      ) : null}
    </div>
  );
};

export default TalentTable;
